/**
 * Builds a case-insensitive regex for fuzzy searching within a target string.
 *
 * The search string is split by whitespace into tokens. Non-alphanumeric characters
 * are stripped from each token. Each token matches as a substring within a word in
 * the target string, and matched words must appear in left-to-right order.
 *
 * Punctuation is ignored in both the search and target strings: "doesnt" matches
 * "doesn't" and vice versa. A space in the search string requires a word boundary
 * in the target: "Grace Church" matches "Grace Fellowship Church" but not
 * "GraceChurch".
 *
 * Returns null if the search string contains no alphanumeric characters.
 */
export function buildSearchRegex(search: string): RegExp | null {
  const tokens = search
    .split(/\s+/)
    .map((t) => t.replace(/[^a-z0-9]/gi, '').toLowerCase())
    .filter((t) => t.length > 0);

  if (tokens.length === 0) {
    return null;
  }

  // Between characters of a single token, allow optional non-alphanumeric,
  // non-space characters (punctuation). This lets "doesnt" match "doesn't".
  const punct = '[^a-z0-9\\s]*';

  const tokenPatterns = tokens.map((token) => token.split('').join(punct));

  // Between tokens: finish the current word, require at least one space
  // (with optional intervening words), then allow leading into the next word.
  const alphaRest = '[a-z0-9]*';
  const alphaRestLazy = '[a-z0-9]*?';
  const separator = `${alphaRest}${punct}\\s+(?:\\S+\\s+)*?${punct}${alphaRestLazy}`;

  const pattern = tokenPatterns.join(separator);
  // Intentionally no 'g' flag: the returned regex is reused across many .test()
  // calls on different strings, and a stateful lastIndex would cause matches to
  // be skipped on alternating calls.
  return new RegExp(pattern, 'i');
}

/**
 * Tests whether the target string matches the given search string.
 * An empty or punctuation-only search matches everything.
 * See {@link buildSearchRegex} for matching rules.
 */
export function matchesSearch({ search, target }: { search: string; target: string }): boolean {
  const regex = buildSearchRegex(search);
  if (!regex) {
    return true;
  }
  return regex.test(target);
}
